import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';

type BusinessType = Tables<'business_types'>;

interface BusinessTypeAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export function BusinessTypeAutocomplete({ value, onChange, placeholder = "Search for business type...", className }: BusinessTypeAutocompleteProps) {
  const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([]);
  const [filteredBusinessTypes, setFilteredBusinessTypes] = useState<BusinessType[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch business types from database
  useEffect(() => {
    const fetchBusinessTypes = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('business_types')
          .select('*')
          .order('name');

        if (error) {
          console.error('Error fetching business types:', error);
        } else {
          setBusinessTypes(data || []);
        }
      } catch (error) {
        console.error('Error fetching business types:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessTypes();
  }, []);

  // Filter business types based on input
  useEffect(() => {
    if (!value) {
      setFilteredBusinessTypes(businessTypes);
    } else {
      const filtered = businessTypes.filter(businessType =>
        businessType.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredBusinessTypes(filtered);
    }
  }, [value, businessTypes]);

  const handleSelect = (businessType: BusinessType) => {
    onChange(businessType.name);
    setOpen(false);
  };

  const handleInputChange = (inputValue: string) => {
    onChange(inputValue);
    setOpen(true);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Input
          placeholder={placeholder}
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          className={cn("bg-white text-black placeholder:text-[#b0b0b0] border-black font-sans", className)}
        />
      </div>
      
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-black rounded-md shadow-lg max-h-60 overflow-auto">
          {loading ? (
            <div className="p-4 text-center text-sm text-gray-500">
              Loading business types...
            </div>
          ) : filteredBusinessTypes.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">
              No business types found
            </div>
          ) : (
            <div className="py-1">
              {filteredBusinessTypes.map((businessType) => (
                <div
                  key={businessType.id}
                  className="relative flex cursor-pointer select-none items-center px-4 py-2 hover:bg-gray-100"
                  onClick={() => handleSelect(businessType)}
                >
                  <span className="text-sm font-medium text-black !text-black" style={{ color: 'black !important' }}>{businessType.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function cn(...classes: (string | undefined)[]) {
  return classes.filter(Boolean).join(' ');
} 